/**
 * Team API — manage assistants/clerks for the authenticated barrister.
 */
import axios from 'axios';
import { api } from './api';

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

const publicTeamApi = axios.create({
  baseURL: `${API_URL}/api/v1/public/team`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'jurivoxa-web' },
});

export interface TeamInviteDetails {
  email: string;
  firstName: string;
  lastName: string;
  roleLabel: string;
  roleLabelDisplay: string;
  permissionLabels: string[];
  expiresAt: string;
  barrister: { firstName: string; lastName: string };
}

export interface TeamInviteSession {
  accessToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    memberType: string;
    avatarUrl: string | null;
    chambersId: string;
    barristerId: string | null;
  };
  chambers: { id: string; name: string; slug: string; logoUrl: string | null; currency: string; jurisdiction: string; timezone: string };
  permissions: string[];
}

export async function getTeamInviteDetails(token: string): Promise<TeamInviteDetails> {
  const res = await publicTeamApi.get(`/invite/${token}`);
  return (res.data as any).data;
}

export async function acceptTeamInvite(data: {
  token: string;
  password: string;
}): Promise<TeamInviteSession> {
  const res = await publicTeamApi.post('/accept-invite', data);
  return (res.data as any).data;
}

export interface TeamMember {
  memberId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  role: string;
  roleLabel: string;
  memberType: string;
  status: string;
  permissions: string[];
  note: string | null;
  assignedAt: string;
  lastLoginAt: string | null;
  isActive: boolean;
  matterCount: number;
}

export interface MatterSummary {
  id: string;
  caseName: string;
  caseNumber: string;
  status: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleLabel: string;
  permissions: string[];
  status: string;
  token: string;
  expiresAt: string;
  createdAt: string;
}

export interface TeamStats {
  total: number;
  active: number;
  suspended: number;
  pending: number;
}

export interface TeamMetadata {
  permissionGroups: Record<string, Record<string, string>>;
  rolePresets: Record<string, string[]>;
  roleLabels: Record<string, string>;
}

export interface EmailLookupResult {
  firstName: string;
  lastName: string;
  phone: string | null;
  memberType: string;
  avatarUrl: string | null;
  chambersName: string;
  isSameChambers: boolean;
}

export async function lookupEmail(email: string): Promise<EmailLookupResult | null> {
  const res = await api.get('/me/team/lookup', { params: { email } });
  return (res.data as any).data ?? null;
}

export async function fetchTeamMetadata(): Promise<TeamMetadata> {
  const res = await api.get('/me/team/metadata');
  return (res.data as any).data;
}

export async function fetchTeamStats(): Promise<TeamStats> {
  const res = await api.get('/me/team/stats');
  return (res.data as any).data;
}

export async function fetchTeam(): Promise<TeamMember[]> {
  const res = await api.get('/me/team');
  return (res.data as any).data ?? [];
}

export async function fetchInvitations(): Promise<TeamInvitation[]> {
  const res = await api.get('/me/team/invitations');
  return (res.data as any).data ?? [];
}

export async function inviteAssistant(data: {
  email: string; firstName: string; lastName: string;
  roleLabel: string; permissions: string[]; note?: string;
}): Promise<TeamInvitation> {
  const res = await api.post('/me/team/invitations', data);
  return (res.data as any).data;
}

export async function cancelInvitation(id: string): Promise<void> {
  await api.delete(`/me/team/invitations/${id}`);
}

export async function updateMemberStatus(memberId: string, action: 'suspend' | 'reactivate'): Promise<void> {
  await api.patch(`/me/team/${memberId}/status`, { action });
}

export async function updateMemberPermissions(memberId: string, permissions: string[], note?: string): Promise<void> {
  await api.patch(`/me/team/${memberId}/permissions`, { permissions, note });
}

export async function removeMember(memberId: string): Promise<void> {
  await api.delete(`/me/team/${memberId}`);
}

export async function fetchMemberActivity(memberId: string): Promise<any[]> {
  const res = await api.get(`/me/team/${memberId}/activity`);
  return (res.data as any).data ?? [];
}

export interface AvailableClerk {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  memberType: string;
  avatarUrl: string | null;
}

export async function fetchAvailableClerks(): Promise<AvailableClerk[]> {
  const res = await api.get('/me/team/available-clerks');
  return (res.data as any).data ?? [];
}

export async function connectClerk(
  clerkUserId: string,
  role: 'primary' | 'assistant' = 'assistant',
  permissions?: string[],
): Promise<TeamMember> {
  const res = await api.post('/me/team/connect', { clerkUserId, role, permissions });
  return (res.data as any).data;
}

export async function fetchMemberMatters(memberId: string): Promise<MatterSummary[]> {
  const res = await api.get(`/me/team/${memberId}/matters`);
  return (res.data as any).data ?? [];
}

export async function transferMatters(
  fromMemberId: string,
  toMemberId?: string,
): Promise<{ transferred: number }> {
  const res = await api.post('/me/team/transfer', { fromMemberId, toMemberId });
  return (res.data as any).data;
}
